import {
  Play,
  Square,
  FileText,
  ChevronDown,
  Settings,
  Save,
  Clock,
  Users,
  MessageSquare,
  RefreshCw,
  Pause,
  AlertCircle,
  CheckCircle2,
  Zap,
  Activity,
} from "lucide-react";
import React, { useRef, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootDispatch, RootState } from "../../store";
import { ConfigInfo } from "../../models/roundRobin.model";
import GenerateTemplateConfiguration from "./GenerateTemplateConfiguration";
import { DocumentFileMap } from "../../models/whatsappTemplate.model";
import socket from "../../utils/socketConnect.utils";
import { clearRoundRobinHeaderFooterBody } from "../../store/slices/roundRobinSlice";

interface WhatsAppAutoMessageSenderFormProps {
  config: ConfigInfo;
  setConfig: React.Dispatch<React.SetStateAction<ConfigInfo>>;
  updateFormData: () => Promise<void>;
  stopCronJob: () => Promise<void>;
  getTemplatesById: (id: string) => Promise<void>;
  selectedTemplateId: string | null;
  setSelectedTemplateId: React.Dispatch<React.SetStateAction<string | null>>;
  documentFile: DocumentFileMap;
  setDocumentFile: React.Dispatch<React.SetStateAction<DocumentFileMap>>;
  setComponentInputs: React.Dispatch<React.SetStateAction<any>>;
  componentInputs: any;
  handleStartCronJob: () => Promise<void>;
  getCronJobStatus: () => Promise<void>;
  isPaused: boolean;
  setIsPaused: React.Dispatch<React.SetStateAction<boolean>>;
  handlePauseCronJob: () => Promise<void>;
  handleResumeCronJob: () => Promise<void>;
}

export const WhatsAppAutoMessageSenderForm: React.FC<
  WhatsAppAutoMessageSenderFormProps
> = ({
  config,
  setConfig,
  documentFile,
  setDocumentFile,
  setComponentInputs,
  componentInputs,
  handleStartCronJob,
  selectedTemplateId,
  updateFormData,
  setSelectedTemplateId,
  getTemplatesById,
  stopCronJob,
  isPaused,
  handlePauseCronJob,
  handleResumeCronJob,
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const dispatch: RootDispatch = useDispatch();
  // Redux selectors
  const isCronJobRunning = useSelector(
    (state: RootState) => state.roundRobin.cronJobStatus
  );


  const whatsappTemplates = useSelector(
    (state: RootState) => state.whatsAppData.templates
  );
  const singleTemplate = useSelector(
    (state: RootState) => state.whatsAppData.singleTemplate
  );
  const isTemplateLoading = useSelector(
    (state: RootState) => state.whatsAppData.loading
  );
  const loadingStates = useSelector(
    (state: RootState) => state.roundRobin.loadingStates
  );
  const configInfo = useSelector(
    (state: RootState) => state.roundRobin.configInfo
  );
  const leadCount = useSelector(
    (state: RootState) => state.leadSlice.leadCount
  );
  const {
    updateData: isUpdatingData,
    startJob: isStartingJob,
    stopJob: isStoppingJob,
    uploadFile: isUploadingFile,
  } = loadingStates;

  const handleSelectFile = () => {
    if (inputRef.current) {
      inputRef.current.value = "";
      inputRef.current.click();
    }
  };

  useEffect(() => {
    if (config.template && whatsappTemplates?.length) {
      const found = whatsappTemplates.find(
        (t: any) => t.name === config.template
      );
      if (found && found.id !== selectedTemplateId) {
        setSelectedTemplateId(found.id);
        getTemplatesById(found.id);
      }
    }
  }, [
    config.template,
    getTemplatesById,
    selectedTemplateId,
    setSelectedTemplateId,
    whatsappTemplates,
  ]);

  // Check if form has unsaved changes
  const arraysAreEqual = (arr1?: any[], arr2?: any[]) => {
    if (!arr1 && !arr2) return true;
    if (!arr1 || !arr2) return false;
    if (arr1.length !== arr2.length) return false;

    return arr1.every((val, index) => val === arr2[index]);
  };

  const hasUnsavedChanges =
    config.cap !== configInfo.cap ||
    config.numberOfDaysBeforeReset !== configInfo.numberOfDaysBeforeReset ||
    config.numberOfTimesToSendMsg !== configInfo.numberOfTimesToSendMsg ||
    config.template !== configInfo.template ||
    config.delay !== configInfo.delay ||
    config.campaignTime !== configInfo.campaignTime ||
    !arraysAreEqual(config.headers, configInfo.headers) ||
    !arraysAreEqual(config.body, configInfo.body) ||
    !arraysAreEqual(config.footer, configInfo.footer);

  const isFormValid =
    config.cap > 0 &&
    config.numberOfDaysBeforeReset > 0 &&
    config.numberOfTimesToSendMsg > 0 &&
    config.delay > 0 &&
    config.template &&
    selectedTemplateId;

  // Determine start button state
  const getStartButtonState = () => {
    if (isUploadingFile) return { text: "Uploading file...", disabled: true };
    if (isUpdatingData) return { text: "Updating Data...", disabled: true };
    if (isStartingJob) return { text: "Starting job...", disabled: true };

    if (hasUnsavedChanges)
      return { text: "Save changes first", disabled: true };
    return { text: "Start Sending", disabled: false };
  };

  const startButtonState = getStartButtonState();

  function formatSeconds(seconds: number) {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    const parts = [];
    if (hrs) parts.push(`${hrs} hr${hrs > 1 ? "s" : ""}`);
    if (mins) parts.push(`${mins} min${mins > 1 ? "s" : ""}`);
    if (secs || (!hrs && !mins))
      parts.push(`${secs} sec${secs > 1 ? "s" : ""}`);
    return parts.join(" ");
  }

  const clearRoundRobinData = async () => {
    await dispatch(clearRoundRobinHeaderFooterBody());
  };

  return (
    <div className="w-full max-w-[1400px] mx-auto">
      {/* Header Section */}
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          {/* Status Badge */}
          <div className="flex items-center gap-3">
            <div
              className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-200 ${
                isCronJobRunning
                  ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                  : "bg-gray-50 text-gray-600 border border-gray-200"
              }`}
            >
              <div
                className={`w-2 h-2 rounded-full transition-all duration-200 ${
                  isCronJobRunning
                    ? "bg-emerald-500 animate-pulse"
                    : "bg-gray-400"
                }`}
              />
              {isCronJobRunning ? "Campaign Active" : "Campaign Inactive"}
            </div>

            {isPaused && (
              <div className="flex items-center gap-2 px-3 py-1.5 bg-orange-50 text-orange-700 rounded-full text-sm font-medium border border-orange-200">
                <Pause className="w-3 h-3" />
                Paused
              </div>
            )}
          </div>

          {/* Unsaved Changes Indicator */}
          {hasUnsavedChanges && (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-amber-50 text-amber-700 rounded-full text-sm font-medium border border-amber-200">
              <AlertCircle className="w-4 h-4" />
              Unsaved changes
            </div>
          )}
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Left Column - Basic Configuration */}
        <div className="lg:col-span-3">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="p-5 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Settings className="w-4 h-4 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-gray-900">
                    Basic Configuration
                  </h3>
                  <p className="text-xs text-gray-500">
                    Core campaign settings
                  </p>
                </div>
              </div>
            </div>

            <div className="p-5 space-y-5">
              {/* Daily Message Cap */}
              <div>
                {/* Campaign Time */}
                <div className="space-y-4 mb-4">
                  <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    <Clock className="w-4 h-4 text-gray-500" />
                    Campaign Time
                  </label>

                  <input
                    type="time"
                    value={config.campaignTime}
                    onChange={(e) =>
                      setConfig({ ...config, campaignTime: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200"
                    step="60"
                    disabled={!!isCronJobRunning}
                  />
                </div>

                <div className="flex items-center gap-2 mb-2 justify-between">
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                    <Users className="w-4 h-4 text-gray-400" />
                    Daily Cap
                  </label>
                  <p className="text-xs text-red-500 mt-1 font-medium">
                    Total leads : {leadCount}
                  </p>
                </div>
                <input
                  type="number"
                  value={config.cap}
                  onChange={(e) =>
                    setConfig({
                      ...config,
                      cap: parseInt(e.target.value) || 0,
                    })
                  }
                  className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 focus:bg-white text-sm transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed placeholder:text-gray-400"
                  placeholder="900"
                  min="1"
                  disabled={!!isCronJobRunning}
                />
              </div>

              {/* Days Before Reset */}
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                  <RefreshCw className="w-4 h-4 text-gray-400" />
                  Days Before Reset
                </label>
                <input
                  type="number"
                  value={config.numberOfDaysBeforeReset}
                  onChange={(e) =>
                    setConfig({
                      ...config,
                      numberOfDaysBeforeReset: parseInt(e.target.value) || 0,
                    })
                  }
                  className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 focus:bg-white text-sm transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed placeholder:text-gray-400"
                  placeholder="30"
                  min="1"
                  disabled={!!isCronJobRunning}
                />
              </div>

              {/* Send Attempts */}
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                  <MessageSquare className="w-4 h-4 text-gray-400" />
                  Send Attempts
                </label>
                <input
                  type="number"
                  value={config.numberOfTimesToSendMsg}
                  onChange={(e) =>
                    setConfig({
                      ...config,
                      numberOfTimesToSendMsg: parseInt(e.target.value) || 0,
                    })
                  }
                  className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 focus:bg-white text-sm transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed placeholder:text-gray-400"
                  placeholder="2"
                  min="1"
                  disabled={!!isCronJobRunning}
                />
              </div>

              {/* Message Delay */}
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                  <Clock className="w-4 h-4 text-gray-400" />
                  Message Delay (seconds)
                </label>
                <input
                  type="number"
                  value={config.delay}
                  onChange={(e) => {
                    const value = parseInt(e.target.value) || 0;
                    setConfig({
                      ...config,
                      delay: value,
                    });
                  }}
                  className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 focus:bg-white text-sm transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed placeholder:text-gray-400"
                  placeholder="5"
                  min="1"
                  disabled={!!isCronJobRunning}
                />
                {config.delay > 60 && (
                  <p className="text-xs text-gray-500 mt-1 font-medium">
                    {formatSeconds(config.delay)}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Middle Column - Template Configuration */}
        <div className="lg:col-span-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="p-5 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                  <FileText className="w-4 h-4 text-purple-600" />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-gray-900">
                    Template Configuration
                  </h3>
                  <p className="text-xs text-gray-500">
                    Select and configure your message template
                  </p>
                </div>
              </div>
            </div>

            <div className="p-5 space-y-5">
              {/* Template Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  WhatsApp Template
                </label>
                <div className="relative">
                  <select
                    value={selectedTemplateId || ""}
                    onChange={async (e) => {
                      const id = e.target.value;
                      setSelectedTemplateId(id);

                      // Reset states when template changes
                      setDocumentFile({});
                      setComponentInputs({});

                      if (id) {
                        await clearRoundRobinData(); // clear first
                        const selectedTemplate = whatsappTemplates?.find(
                          (template: any) => template?.id === id
                        );
                        setConfig((prev) => ({
                          ...prev,
                          template: selectedTemplate?.name || "",
                        }));
                        getTemplatesById(id); // fetch new template data
                      }
                    }}
                    className="w-full appearance-none bg-gray-50 border border-gray-200 rounded-lg py-2.5 px-3 pr-8 text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 focus:bg-white transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={!!isTemplateLoading || !!isCronJobRunning}
                  >
                    <option value="">Select a template</option>
                    {whatsappTemplates?.map((template: any) => (
                      <option key={template?.id} value={template?.id}>
                        {template?.name}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-3 top-3 h-4 w-4 text-gray-400 pointer-events-none" />
                </div>

                {isTemplateLoading && (
                  <div className="flex items-center mt-3 p-3 bg-blue-50 rounded-lg border border-blue-100">
                    <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full mr-3"></div>
                    <span className="text-sm text-blue-700">
                      Loading template...
                    </span>
                  </div>
                )}
              </div>

              {/* Template Configuration Component */}
              <GenerateTemplateConfiguration
                singleTemplate={singleTemplate}
                setComponentInputs={setComponentInputs}
                documentFile={documentFile}
                setDocumentFile={setDocumentFile}
                handleSelectFile={handleSelectFile}
                inputRef={inputRef}
                componentInputs={componentInputs}
                setConfig={setConfig}
              />
            </div>
          </div>
        </div>

        {/* Right Column - Controls & Status */}
        <div className="lg:col-span-3">
          <div className="space-y-6">
            {/* Campaign Controls */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="p-5 border-b border-gray-100">
                <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                  <Activity className="w-4 h-4 text-gray-600" />
                  Campaign Controls
                </h3>
              </div>

              <div className="p-5 space-y-3">
                {/* Time Selector */}

                {/* Save Changes Button */}
                <button
                  onClick={updateFormData}
                  // disabled={
                  //   isUpdatingData || isCronJobRunning || !hasUnsavedChanges
                  // }
                  className={`w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-all duration-200 text-sm ${
                    isUpdatingData
                      ? "bg-gray-50 text-gray-400 border border-gray-200 cursor-not-allowed"
                      : hasUnsavedChanges && !isCronJobRunning
                      ? "bg-gray-900 text-white border border-gray-900 hover:bg-gray-800 shadow-sm"
                      : "bg-gray-50 text-gray-400 border border-gray-200 cursor-not-allowed"
                  }`}
                >
                  {isUpdatingData ? (
                    <>
                      <div className="w-4 h-4 border-2 border-gray-300 border-t-gray-500 rounded-full animate-spin"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      {hasUnsavedChanges ? "Save Changes" : "No Changes"}
                    </>
                  )}
                </button>

                {/* Start Campaign */}
                {!isCronJobRunning && !isPaused && (
                  <button
                    onClick={handleStartCronJob}
                    disabled={startButtonState.disabled}
                    className={`w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-medium transition-all duration-200 text-sm border ${
                      startButtonState.disabled
                        ? "bg-slate-50 text-slate-400 border-slate-200 cursor-not-allowed"
                        : "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100 shadow-sm"
                    }`}
                  >
                    {isUploadingFile ? (
                      <>
                        <div className="w-4 h-4 border-2 border-emerald-300 border-t-emerald-600 rounded-full animate-spin"></div>
                        Processing...
                      </>
                    ) : isStartingJob ? (
                      <>
                        <div className="w-4 h-4 border-2 border-emerald-300 border-t-emerald-600 rounded-full animate-spin"></div>
                        Starting...
                      </>
                    ) : (
                      <>
                        <Play className="w-4 h-4" />
                        {startButtonState.text}
                      </>
                    )}
                  </button>
                )}

                {/* Resume Campaign */}
                {isPaused && (
                  <button
                    onClick={handleResumeCronJob}
                    disabled={
                      loadingStates.startJob ||
                      isUploadingFile ||
                      isStartingJob ||
                      hasUnsavedChanges
                    }
                    className={`w-full inline-flex items-center justify-center gap-3 px-6 py-3.5 rounded-xl font-medium transition-all duration-200 text-sm border-2 ${
                      loadingStates.startJob ||
                      isUploadingFile ||
                      isStartingJob ||
                      hasUnsavedChanges
                        ? "bg-slate-50 text-slate-400 border-slate-200 cursor-not-allowed"
                        : "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100 hover:border-emerald-300 shadow-sm hover:shadow-md"
                    }`}
                    title={
                      hasUnsavedChanges ? "Save changes before resuming" : ""
                    }
                  >
                    {loadingStates.startJob ? (
                      <>
                        <div className="w-5 h-5 border-2 border-slate-300 border-t-slate-500 rounded-full animate-spin"></div>
                        <span className="font-semibold">Resuming...</span>
                      </>
                    ) : isUploadingFile ? (
                      <>
                        <div className="w-4 h-4 border-2 border-emerald-300 border-t-emerald-600 rounded-full animate-spin"></div>
                        <span className="font-semibold">Processing...</span>
                      </>
                    ) : isStartingJob ? (
                      <>
                        <div className="w-4 h-4 border-2 border-emerald-300 border-t-emerald-600 rounded-full animate-spin"></div>
                        <span className="font-semibold">Starting...</span>
                      </>
                    ) : (
                      <>
                        <Play className="w-5 h-5" />
                        <span className="font-semibold">Resume Campaign</span>
                      </>
                    )}
                  </button>
                )}

                {isCronJobRunning && !isPaused && (
                  <button
                    onClick={stopCronJob}
                    disabled={isStoppingJob || loadingStates.deleteFiles}
                    className={`w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-all duration-200 text-sm ${
                      isStoppingJob || loadingStates.deleteFiles
                        ? "bg-red-50 text-red-400 border border-red-200 cursor-not-allowed"
                        : "bg-red-50 text-red-600 border border-red-200 hover:bg-red-100 shadow-sm"
                    }`}
                  >
                    {isStoppingJob || loadingStates.deleteFiles ? (
                      <>
                        <div className="w-4 h-4 border-2 border-red-300 border-t-red-500 rounded-full animate-spin"></div>
                        {loadingStates.deleteFiles
                          ? "Please wait..."
                          : "Stopping..."}
                      </>
                    ) : (
                      <>
                        <Square className="w-4 h-4" />
                        Stop Campaign
                      </>
                    )}
                  </button>
                )}

                {/* Pause Campaign */}
                {isCronJobRunning && !isPaused && (
                  <button
                    onClick={handlePauseCronJob}
                    disabled={loadingStates.startJob}
                    className={`w-full inline-flex items-center justify-center gap-3 px-6 py-3.5 rounded-xl font-medium transition-all duration-200 text-sm border-2 ${
                      loadingStates.startJob
                        ? "bg-slate-50 text-slate-400 border-slate-200 cursor-not-allowed"
                        : "bg-orange-50 text-orange-700 border-orange-200 hover:bg-orange-100 hover:border-orange-300 shadow-sm hover:shadow-md"
                    }`}
                  >
                    {loadingStates.startJob ? (
                      <>
                        <div className="w-5 h-5 border-2 border-slate-300 border-t-slate-500 rounded-full animate-spin"></div>
                        <span className="font-semibold">Pausing...</span>
                      </>
                    ) : (
                      <>
                        <Pause className="w-5 h-5" />
                        <span className="font-semibold">Pause Campaign</span>
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
            {/* Current Configuration */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-lg font-semibold text-slate-900">
                  Current Configuration
                </h4>
                {isFormValid && (
                  <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                )}
              </div>

              <div className="space-y-4">
                <div className="flex justify-between items-center py-2 border-b border-slate-100">
                  <span className="text-sm text-slate-600">Daily Cap:</span>
                  <span className="text-sm font-medium text-slate-900">
                    {configInfo.cap}
                  </span>
                </div>

                <div className="flex justify-between items-center py-2 border-b border-slate-100">
                  <span className="text-sm text-slate-600">Reset Days:</span>
                  <span className="text-sm font-medium text-slate-900">
                    {configInfo.numberOfDaysBeforeReset}
                  </span>
                </div>

                <div className="flex justify-between items-center py-2 border-b border-slate-100">
                  <span className="text-sm text-slate-600">Send Attempts:</span>
                  <span className="text-sm font-medium text-slate-900">
                    {configInfo.numberOfTimesToSendMsg}
                  </span>
                </div>

                <div className="flex justify-between items-center py-2 border-b border-slate-100">
                  <span className="text-sm text-slate-600">Delay:</span>
                  <span className="text-sm font-medium text-slate-900">
                    {configInfo.delay}s
                  </span>
                </div>

                <div className="flex justify-between items-center py-2">
                  <span className="text-sm text-slate-600">Template:</span>
                  <span
                    className="text-sm font-medium text-slate-900 text-right max-w-24 truncate"
                    title={configInfo.template}
                  >
                    {configInfo.template || "None"}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
